import React, { createContext, useState, useContext, useEffect, useMemo } from 'react';

const LanguageContext = createContext();

const supportedLanguages = {
  en: {
    language: 'English',
    translations: {
      common: {
        appName: 'Personal Finance: PCM Planning, Controlling & Monitoring',
        save: 'Save',
        cancel: 'Cancel',
        confirm: 'Confirm',
        close: 'Close',
        add: 'Add',
        edit: 'Edit',
        delete: 'Delete',
        planning: 'Planning',
        planningDescription: 'Plan your finances with our advanced budgeting tools',
        monitoring: 'Monitoring',
        monitoringDescription: 'Track your expenses and income in real-time',
        control: 'Control',
        controlDescription: 'Manage your financial goals and stay on track',
        getStarted: 'Get Started',
        whatOurUsersSay: 'What Our Users Say',
        testimonialsDescription: 'Real stories from real users who trust PCM Planning',
        pricingPlans: 'Pricing Plans',
        pricingDescription: 'Choose the plan that fits your needs',
        choosePlan: 'Choose Plan',
        monthly: 'Monthly',
        yearly: 'Yearly',
      },
      auth: {
        login: 'Login',
        register: 'Register',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
      },
      dashboard: {
        title: 'Dashboard',
        welcome: 'Welcome',
        totalIncome: 'Total Income',
        totalExpenses: 'Total Expenses',
        netBalance: 'Net Balance',
      },
      transactions: {
        title: 'Transactions',
        category: 'Category',
        amount: 'Amount',
        description: 'Description',
        date: 'Date',
        type: 'Type',
      },
      accounts: {
        title: 'Accounts',
        balance: 'Balance',
        institution: 'Institution',
        accountType: 'Account Type',
        transactions: 'Transactions',
        addAccount: 'Add Account',
        editAccount: 'Edit Account',
        totalBalance: 'Total Balance',
      },
      budgets: {
        title: 'Budgets',
        monthlyBudget: 'Monthly Budget',
        spent: 'Spent',
        remaining: 'Remaining',
      },
      reports: {
        title: 'Reports',
      },
      workspaces: {
        title: 'Workspaces',
        personal: 'Personal',
        team: 'Team',
        add: 'Add Workspace',
      },
      docs: {
        title: 'Documentation',
      },
      debts: {
        title: 'Debts',
        amount: 'Amount',
        monthlyPayment: 'Monthly Payment',
        dueDate: 'Due Date',
      },
      footer: {
        description: 'Personal Finance Management System - Plan, Monitor, and Control your finances effectively.',
        quickLinks: 'Quick Links',
        aboutUs: 'About Us',
        features: 'Features',
        pricing: 'Pricing',
        contact: 'Contact',
        email: 'Email:',
        phone: 'Phone:',
        copyright: '  {year} Personal Finance. All rights reserved.',
      },
    },
  },
  es: {
    language: 'Español',
    translations: {
      common: {
        appName: 'Análisis Personales: PCM Planificación, Control y Monitoreo',
        save: 'Guardar',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        close: 'Cerrar',
        add: 'Agregar',
        edit: 'Editar',
        delete: 'Eliminar',
        planning: 'Planificación',
        planningDescription: 'Planifique sus finanzas con nuestras herramientas avanzadas de presupuesto',
        monitoring: 'Monitoreo',
        monitoringDescription: 'Siga sus gastos e ingresos en tiempo real',
        control: 'Control',
        controlDescription: 'Mantenga el rumbo con herramientas efectivas de control financiero',
        getStarted: 'Comenzar',
        whatOurUsersSay: 'Lo que dicen nuestros usuarios',
        testimonialsDescription: 'Historias reales de usuarios que confían en PCM Planning',
        pricingPlans: 'Planes de precios',
        pricingDescription: 'Elija el plan que se adapte a sus necesidades',
        choosePlan: 'Elegir Plan',
        monthly: 'Mensual',
        yearly: 'Anual',
        // Textos de seguridad eliminados según solicitud
        consultant: 'Hablar con un Consultor',
        features: 'Características',
        talkToConsultant: 'Hablar con un Consultor',
        firstConsultFree: '¡Primera consulta GRATIS!',
        new: 'Nuevo',
        moreActions: 'Más acciones',
        import: 'Importar',
      },
      auth: {
        login: 'Iniciar Sesión',
        register: 'Registrarse',
        email: 'Correo Electrónico',
        password: 'Contraseña',
        confirmPassword: 'Confirmar Contraseña',
        logout: 'Cerrar Sesión',
        firstName: 'Nombre',
        lastName: 'Apellido',
        acceptTerms: 'Acepto los términos y condiciones',
        forgotPassword: 'Olvidé mi contraseña',
        backToHome: 'Volver al inicio',
        clientPortal: 'Ingreso Clientes',
        clientPortalDescription: 'Acceda a sus herramientas personalizadas de gestión financiera y tome el control de su futuro financiero',
        welcomeToFinancial: 'Bienvenido a su futuro financiero',
        joinThousands: 'Únase a miles de clientes que han transformado sus vidas financieras con nuestro enfoque PCM.',
        clientBenefits: 'Beneficios para Clientes',
        /* Referencia a acceso seguro eliminada */
        accessAnywhere: 'Acceso desde cualquier lugar',
        accessAnywhereSub: 'Compatible con todos los dispositivos, para que pueda administrar sus finanzas en cualquier momento',
        dashboardAccess: 'Acceso al Inicio',
        dashboardAccessSub: 'Vea todos sus datos financieros en un sólo lugar con informes detallados',
        supportAccess: 'Soporte prioritario',
        supportAccessSub: 'Obtenga ayuda personalizada cuando la necesite de nuestro equipo de expertos',
        createAccount: 'Crear Cuenta',
        signIn: 'Iniciar Sesión',
        signInRegister: 'Iniciar Sesión / Registrarse',
      },
      consultant: {
        title: 'Agende una Consulta',
        subtitle: 'Nuestros asesores financieros están aquí para ayudarle',
        step1: 'Seleccione un consultor',
        step2: 'Seleccione tema y fecha',
        step3: 'Detalles de contacto',
        step4: 'Confirmación',
        selectConsultant: 'Seleccione un consultor',
        selectTopic: 'Seleccione un tema',
        personalFinance: 'Finanzas Personales',
        investment: 'Inversiones',
        retirement: 'Planificación de Jubilación',
        taxes: 'Impuestos',
        debt: 'Manejo de Deudas',
        other: 'Otro',
        otherTopic: 'Especifique otro tema',
        selectDate: 'Seleccione una fecha',
        selectTime: 'Seleccione una hora',
        consultationMethod: 'Método de consulta',
        phone: 'Llamada Telefónica',
        video: 'Videollamada',
        chat: 'Chat en Vivo',
        inperson: 'Reunión en Persona',
        yourName: 'Su nombre',
        email: 'Correo electrónico',
        phoneNumber: 'Número de teléfono',
        message: 'Mensaje o preguntas',
        back: 'Atrás',
        next: 'Siguiente',
        submit: 'Enviar Solicitud',
        meetOurTeam: 'Conozca a Nuestro Equipo',
        financialAdvisor: 'Asesor Financiero',
        confirmationMessage: 'Su solicitud de consulta ha sido enviada con éxito',
        confirmationDetails: 'Hemos enviado un correo electrónico de confirmación con los detalles de su cita',
        retryMessage: 'Hubo un problema al enviar su solicitud. Por favor intente de nuevo.',
        years: 'años de experiencia',
        expertise: 'Especialidad',
        biography: 'Biografía',
        loading: 'Procesando su solicitud...',
        requiredField: 'Este campo es requerido',
      },
      features: {
        title: 'Nuestras Características',
        subtitle: 'Herramientas poderosas para gestionar sus finanzas personales',
        coreFeatures: 'Características Principales',
        methodology: 'Metodología PCM',
        additionalFeatures: 'Características Adicionales',
        featureComparison: 'Comparación de Planes',
        planningTitle: 'Planificación',
        planningDesc: 'Establezca objetivos financieros claros y cree un plan para alcanzarlos',
        monitoringTitle: 'Monitoreo',
        monitoringDesc: 'Seguimiento de sus gastos e ingresos en tiempo real',
        controlTitle: 'Control',
        controlDesc: 'Mantenga el rumbo con herramientas efectivas de control financiero',
        budgetingTitle: 'Presupuestos',
        budgetingDesc: 'Cree presupuestos personalizados para diferentes categorías y períodos',
        expenseTitle: 'Seguimiento de Gastos',
        expenseDesc: 'Registre y categorice sus gastos automáticamente',
        savingsTitle: 'Metas de Ahorro',
        savingsDesc: 'Establezca metas de ahorro y realice un seguimiento de su progreso',
        reportingTitle: 'Informes Detallados',
        reportingDesc: 'Obtenga informes y análisis detallados sobre sus finanzas',
        alertsTitle: 'Alertas Personalizadas',
        alertsDesc: 'Reciba alertas y recordatorios',
        free: 'Gratis',
        basic: 'Básico',
        premium: 'Premium',
        enterprise: 'Empresarial',
        featureIncluded: 'Incluido',
        featureNotIncluded: 'No incluido',
        getStarted: 'Comenzar',
        contactSales: 'Contactar Ventas',
      },
      welcome: {
        title: 'Bienvenido',
        subtitle: 'Gracias por unirse a nuestra comunidad financiera. Juntos, mejoraremos su bienestar financiero.',
        planning: 'Planificación',
        planningDesc: 'Establezca objetivos financieros claros y cree presupuestos realistas para alcanzarlos',
        monitoring: 'Monitoreo',
        monitoringDesc: 'Realice un seguimiento de sus gastos e ingresos en tiempo real con visualizaciones intuitivas',
        control: 'Control',
        controlDesc: 'Mantenga el rumbo con herramientas efectivas de control financiero',
        getStarted: 'Comenzar',
        firstStep: 'Su primer paso hacia el éxito financiero',
        quickAccess: 'Acceso Rápido',
        dashboardBtn: 'Ir al Panel',
        settingsBtn: 'Configuración',
        settings: {
          title: 'Configuración',
        },
        doNext: 'Qué hacer a continuación',
        exploreFeatures: 'Explorar características',
        setupBudget: 'Configurar presupuesto',
        setupBudgetDesc: 'Comience a crear su primer presupuesto mensual',
        addAccounts: 'Agregar cuentas',
        addAccountsDesc: 'Conecte sus cuentas bancarias para un seguimiento automático',
        setGoals: 'Establecer metas',
        setGoalsDesc: 'Defina sus objetivos financieros a corto y largo plazo',
      },
      dashboard: {
        title: 'Inicio',
        welcome: 'Bienvenido',
        totalIncome: 'Ingreso Total',
        totalExpenses: 'Gastos Totales',
        netBalance: 'Balance Neto',
        overview: 'Resumen',
      },
      accounts: {
        title: 'Cuentas',
      },
      finance: {
        title: 'Finanzas',
      },
      workspaces: {
        title: 'Espacios de Trabajo',
        personal: 'Personal',
        team: 'Equipo',
        add: 'Agregar Espacio de Trabajo',
      },
      docs: {
        title: 'Documentación',
      },
      settings: {
        title: 'Configuración',
        theme: 'Tema',
        language: 'Idioma',
        currency: 'Moneda',
        profile: 'Perfil',
        import: 'Importar',
      },
      profile: {
        title: 'Perfil',
      },
      transactions: {
        title: 'Transacciones',
        category: 'Categoría',
        amount: 'Monto',
        description: 'Descripción',
        date: 'Fecha',
        type: 'Tipo',
      },
      accounts: {
        title: 'Cuentas',
        balance: 'Balance',
        institution: 'Institución',
        accountType: 'Tipo de Cuenta',
        transactions: 'Transacciones',
        addAccount: 'Agregar Cuenta',
        editAccount: 'Editar Cuenta',
        totalBalance: 'Balance Total',
      },
      budgets: {
        title: 'Presupuestos',
        monthlyBudget: 'Presupuesto Mensual',
        spent: 'Gastado',
        remaining: 'Restante',
      },
      debts: {
        title: 'Deudas',
        amount: 'Cantidad',
        monthlyPayment: 'Pago Mensual',
        dueDate: 'Fecha de Vencimiento',
      },
      footer: {
        description: 'Sistema de Gestión de Finanzas Personales - Planifique, Monitoree y Controle sus finanzas de manera efectiva.',
        quickLinks: 'Enlaces Rápidos',
        aboutUs: 'Sobre Nosotros',
        features: 'Características',
        pricing: 'Precios',
        contact: 'Contacto',
        email: 'Correo:',
        phone: 'Teléfono:',
        copyright: '  {year} Finanzas Personales. Todos los derechos reservados.',
      },
    },
  },
  pt: {
    language: 'Português',
    translations: {
      common: {
        appName: 'Finanças Pessoais: PCM Planejamento, Controle e Monitoramento',
        save: 'Salvar',
        cancel: 'Cancelar',
        confirm: 'Confirmar',
        close: 'Fechar',
        add: 'Adicionar',
        edit: 'Editar',
        delete: 'Excluir',
      },
      auth: {
        login: 'Entrar',
        register: 'Registrar',
        email: 'E-mail',
        password: 'Senha',
        confirmPassword: 'Confirmar Senha',
      },
      dashboard: {
        title: 'Painel',
        welcome: 'Bem-vindo',
        totalIncome: 'Renda Total',
        totalExpenses: 'Despesas Totais',
        netBalance: 'Saldo Líquido',
      },
      transactions: {
        title: 'Transações',
        category: 'Categoria',
        amount: 'Valor',
        description: 'Descrição',
        date: 'Data',
        type: 'Tipo',
      },
      budgets: {
        title: 'Orçamentos',
        monthlyBudget: 'Orçamento Mensual',
        spent: 'Gasto',
        remaining: 'Restante',
      },
      debts: {
        title: 'Dívidas',
        amount: 'Valor',
        monthlyPayment: 'Pagamento Mensual',
        dueDate: 'Data de Vencimento',
      },
    },
  },
  fr: {
    language: 'Français',
    translations: {
      common: {
        appName: 'Finance Personnelle: PCM Planification, Contrôle et Suivi',
        save: 'Enregistrer',
        cancel: 'Annuler',
        confirm: 'Confirmer',
        close: 'Fermer',
        add: 'Ajouter',
        edit: 'Modifier',
        delete: 'Supprimer',
      },
      auth: {
        login: 'Connexion',
        register: 'S\'inscrire'
      },
      common: {
        save: 'Enregistrer',
        cancel: 'Annuler',
        confirm: 'Confirmer',
        close: 'Fermer',
        add: 'Ajouter',
        edit: 'Modifier',
        delete: 'Supprimer',
        back: 'Retour'
      }
    },
    common: {
      save: 'Enregistrer',
      cancel: 'Annuler',
      confirm: 'Confirmer',
      close: 'Fermer',
  },
    delete: 'Supprimer',
    back: 'Retour'
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('es');
  const [translationsLoaded, setTranslationsLoaded] = useState(false);

  const changeLanguage = (newLanguage) => {
    // Validate the language is supported
    if (!supportedLanguages[newLanguage]) {
      console.warn(`Language ${newLanguage} is not supported. Defaulting to English.`);
      newLanguage = 'en';
    }
    
    console.log(`Changing language to: ${newLanguage}`);
    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    
    // Update HTML lang attribute for accessibility
    if (typeof document !== 'undefined') {
      document.documentElement.lang = newLanguage;
    }
    
    // Dispatch a custom event that components can listen for
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('languageChange', { detail: { language: newLanguage } }));
    }
    
    // Set a flag to indicate translations are loaded
    setTranslationsLoaded(true);
  };

  const t = (key, fallbackText = '') => {
    const translations = supportedLanguages[language]?.translations;
    if (!translations) return fallbackText || key;
    
    // Check if key contains a dot (e.g., 'auth.clientPortal')
    if (key.includes('.')) {
      const [section, subKey] = key.split('.');
      if (translations[section] && translations[section][subKey]) {
        return translations[section][subKey];
      }
    } else {
      // Original behavior - assume 'common' section if no dot
      const sectionTranslations = translations['common'];
      if (sectionTranslations && sectionTranslations[key]) {
        return sectionTranslations[key];
      }
    }
    
    // Return fallback text or key if no translation found
    return fallbackText || key;
  };

  // Function to determine language based on country
  const getLanguageForCountry = (country) => {
    // Make sure country is a string and trim any whitespace
    if (!country || typeof country !== 'string') {
      console.warn('Invalid country provided to getLanguageForCountry:', country);
      return 'en';
    }
    
    // Ensure consistent format for lookup
    const formattedCountry = country.trim();
    
    const countryToLanguage = {
      'United States': 'en',
      'Canada': 'en',
      'United Kingdom': 'en',
      'Australia': 'en',
      'New Zealand': 'en',
      'Mexico': 'es',
      'Spain': 'es',
      'Argentina': 'es',
      'Chile': 'es',
      'Colombia': 'es',
      'Peru': 'es',
      'Brazil': 'pt',
      'Portugal': 'pt',
      // Add some additional Spanish-speaking countries
      'Venezuela': 'es',
      'Ecuador': 'es',
      'Bolivia': 'es',
      'Paraguay': 'es',
      'Uruguay': 'es',
      'Costa Rica': 'es',
      'Panama': 'es'
    };
    
    const language = countryToLanguage[formattedCountry] || 'en';
    console.log(`Country ${formattedCountry} maps to language: ${language}`);
    return language;
  };

  // Update language when country changes
  const updateLanguageFromCountry = (country) => {
    console.log('Updating language from country:', country);
    
    if (!country) {
      console.warn('Empty country provided to updateLanguageFromCountry');
      return;
    }
    
    const newLanguage = getLanguageForCountry(country);
    console.log(`Switching language from ${language} to ${newLanguage} based on country: ${country}`);
    
    // Force language change even if it's the same as current language
    changeLanguage(newLanguage);
    
    // Provide visual confirmation to user
    if (typeof window !== 'undefined') {
      const event = new CustomEvent('languageChanged', { detail: { language: newLanguage, country } });
      window.dispatchEvent(event);
    }
  };

  useEffect(() => {
    // Check for saved language first
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && supportedLanguages[savedLanguage]) {
      setLanguage(savedLanguage);
      return;
    }

    // If no saved language, try to get country from localStorage
    const savedCountry = localStorage.getItem('country');
    if (savedCountry) {
      updateLanguageFromCountry(savedCountry);
    }
  }, []);

  // 7 sep 2026 (Augusto: "aun siento la respuesta muy lenta... en mi
  // celular"): este value se pasaba como objeto literal nuevo en CADA
  // render de LanguageProvider -- como este contexto envuelve casi toda
  // la app y useLanguage()/t() se usa en prácticamente todas las
  // páginas, un objeto nuevo en cada render fuerza a TODOS esos
  // consumidores a re-renderizar aunque el idioma no haya cambiado
  // (React compara por identidad de referencia, no por contenido). Se
  // memoiza con useMemo -- solo cambia de identidad cuando `language`
  // realmente cambia, no en cualquier otro re-render de este provider.
  const value = useMemo(
    () => ({ language, changeLanguage, t, updateLanguageFromCountry }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
